import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  BarChart3, Receipt, Camera, PlusCircle, ListFilter, PieChart, 
  Tag, Wallet, Image, Zap, Settings, Crown, MessageSquare,
  ArrowRight, CheckCircle, Smartphone, Cloud, FileText
} from 'lucide-react';

const HowToUse = () => {
  const menuItems = [
    {
      icon: BarChart3,
      name: 'Dashboard',
      description: 'Your financial overview. See total income, expenses, and net balance for the current month. Quick access to recent transactions.',
      color: 'text-blue-400',
      bg: 'bg-blue-500/20'
    },
    {
      icon: ListFilter,
      name: 'Transactions',
      description: 'View all your income and expenses. Filter by date, category, or type. Edit or delete entries.',
      color: 'text-green-400',
      bg: 'bg-green-500/20'
    },
    {
      icon: FileText,
      name: 'Invoices',
      description: 'Create professional invoices for your clients. Track payments, send reminders, and generate PDF invoices with your company branding.',
      color: 'text-indigo-400',
      bg: 'bg-indigo-500/20'
    },
    {
      icon: PieChart,
      name: 'Analytics',
      description: 'Visual charts showing your spending patterns. See expense breakdown by category and monthly trends.',
      color: 'text-purple-400',
      bg: 'bg-purple-500/20'
    },
    {
      icon: Zap,
      name: 'Smart Rules',
      description: 'Automate expense categorization. Create rules like "If merchant contains Amazon, set category to Shopping".',
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/20'
    },
    {
      icon: Image,
      name: 'Receipts',
      description: 'Gallery of all scanned receipt images. Search, filter, and view receipt details.',
      color: 'text-pink-400',
      bg: 'bg-pink-500/20'
    },
    {
      icon: Wallet,
      name: 'Budgets',
      description: 'Set spending limits for each category. Get alerts when approaching your budget.',
      color: 'text-orange-400',
      bg: 'bg-orange-500/20'
    },
    {
      icon: Tag,
      name: 'Categories',
      description: 'Create and manage custom expense categories. Assign colors and icons.',
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/20'
    },
    {
      icon: Settings,
      name: 'Settings',
      description: 'Update your profile, change password, enable two-factor authentication.',
      color: 'text-gray-400',
      bg: 'bg-gray-500/20'
    },
    {
      icon: MessageSquare,
      name: 'Support',
      description: 'Contact our support team if you have questions or issues.',
      color: 'text-indigo-400',
      bg: 'bg-indigo-500/20'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Scan a Receipt',
      description: 'Click the camera button on Dashboard or go to Scan. Take a photo of your receipt or upload an image.',
      icon: Camera,
      tips: ['Hold phone steady', 'Ensure good lighting', 'Include all text on receipt']
    },
    {
      number: 2,
      title: 'Review & Confirm',
      description: 'Our AI automatically extracts the merchant name, date, total amount, and items. Review and edit if needed.',
      icon: CheckCircle,
      tips: ['Check the total amount', 'Verify the date', 'Select correct category']
    },
    {
      number: 3,
      title: 'Track Your Spending',
      description: 'View your expenses on Dashboard and Analytics. See where your money goes each month.',
      icon: PieChart,
      tips: ['Check Analytics weekly', 'Set budget limits', 'Review transactions monthly']
    }
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />
      <div className="pt-20 pb-12 px-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Receipt className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">How to Use FinScan USA</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your complete guide to tracking expenses with AI-powered receipt scanning
          </p>
        </div>

        {/* Quick Start */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Smartphone className="w-6 h-6 mr-2 text-indigo-400" />
            Quick Start Guide
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.number} className="relative">
                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl h-full">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold mr-3">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">{step.description}</p>
                  <div className="space-y-2">
                    {step.tips.map((tip, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                        {tip}
                      </div>
                    ))}
                  </div>
                </div>
                {step.number < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-indigo-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Scanning Receipt Demo */}
        <div className="mb-16 p-8 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-3xl">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Camera className="w-6 h-6 mr-2 text-indigo-400" />
            How to Scan a Receipt
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-white">Open Scanner</h4>
                  <p className="text-sm text-muted-foreground">Click the purple camera button on Dashboard or navigate to "Scan Receipt"</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-white">Capture or Upload</h4>
                  <p className="text-sm text-muted-foreground">Use your camera to take a photo, or upload an existing image from your device</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-white">AI Processing</h4>
                  <p className="text-sm text-muted-foreground">Our AI reads the receipt and extracts: merchant, date, total, and line items</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-white">Review & Save</h4>
                  <p className="text-sm text-muted-foreground">Check the extracted data, select a category, and save the expense</p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/30 rounded-2xl p-6 border border-white/10">
              <div className="text-center">
                <div className="w-24 h-32 bg-white/10 rounded-lg mx-auto mb-4 flex items-center justify-center border-2 border-dashed border-white/30">
                  <Receipt className="w-12 h-12 text-white/50" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">Receipt Preview Area</p>
                <div className="text-left space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Merchant:</span>
                    <span className="text-white">Walmart</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date:</span>
                    <span className="text-white">Feb 27, 2025</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total:</span>
                    <span className="text-green-400 font-semibold">$45.99</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Category:</span>
                    <span className="text-indigo-400">Groceries</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Creating Invoices Guide */}
        <div className="mb-16 p-8 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border border-indigo-500/30 rounded-3xl">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <FileText className="w-6 h-6 mr-2 text-indigo-400" />
            How to Create Invoices
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-white">Set Up Your Company</h4>
                  <p className="text-sm text-muted-foreground">Go to Invoices → Settings. Enter your company name, address, phone, and email. Upload your logo for branded invoices.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-white">Add Clients</h4>
                  <p className="text-sm text-muted-foreground">Go to Clients tab and add your customers with their name, email, address, and phone number.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-white">Create Invoice</h4>
                  <p className="text-sm text-muted-foreground">Click "New Invoice", select a client, add services/items with quantities and prices. Choose a template style.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-white">Send & Track</h4>
                  <p className="text-sm text-muted-foreground">Download PDF, send via email, and track payment status. Mark as paid when payment is received.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-black/30 rounded-2xl p-6 border border-white/10">
                <h4 className="font-semibold text-white mb-4">Invoice Features</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">10 professional PDF templates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">Company logo on invoices</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">Auto-calculate taxes & discounts</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">Payment reminders via email</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">Reports by client, service, date</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-muted-foreground">Smart Price suggestions</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-start space-x-2">
                  <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-400 text-sm">Free Plan Limit</h4>
                    <p className="text-xs text-muted-foreground">Create up to 20 invoices for free. Upgrade to Premium for unlimited invoices.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Menu Guide */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <ListFilter className="w-6 h-6 mr-2 text-indigo-400" />
            Menu Guide
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {menuItems.map((item) => (
              <div key={item.name} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${item.color}`}>{item.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pro Features */}
        <div className="mb-16 p-8 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-3xl">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Crown className="w-6 h-6 mr-2 text-yellow-400" />
            Pro Features
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-white">Unlimited Receipt Scans</h4>
                <p className="text-sm text-muted-foreground">Free users: 10/month. Pro: Unlimited</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-white">Cloud Backup</h4>
                <p className="text-sm text-muted-foreground">Your receipts are safely stored in the cloud</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-white">Export to PDF/CSV</h4>
                <p className="text-sm text-muted-foreground">Download your data for tax preparation</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-white">Priority Support</h4>
                <p className="text-sm text-muted-foreground">Get help faster with priority queue</p>
              </div>
            </div>
          </div>
          
          <Link
            to="/subscription"
            className="inline-flex items-center mt-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Pro
          </Link>
        </div>

        {/* Tips */}
        <div className="p-8 bg-white/5 border border-white/10 rounded-3xl">
          <h2 className="text-2xl font-bold text-white mb-6">💡 Tips for Best Results</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Take photos in good lighting for accurate scanning</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Keep receipts flat and avoid wrinkles</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Create Smart Rules to auto-categorize recurring expenses</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Set monthly budgets to control your spending</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Review Analytics weekly to spot spending patterns</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-muted-foreground">Install the app on your phone for quick access</p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <Link
            to="/support"
            className="inline-flex items-center bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-6 py-3 rounded-full transition-colors"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;
